import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Shield } from 'lucide-react';
import apiService from '../services/api';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

/**
 * AuthCallback component handles OAuth callback from Emergent Auth.
 * It processes the session_id from the URL fragment, exchanges it for
 * user data, and redirects to the main app.
 */
const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Use ref to prevent double-processing in React StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Get session_id from URL hash fragment
        const hash = window.location.hash;
        const sessionIdMatch = hash.match(/session_id=([^&]+)/);
        
        if (!sessionIdMatch) {
          console.error('No session_id found in URL');
          navigate('/', { replace: true });
          return;
        }

        const sessionId = sessionIdMatch[1];
        console.log('Processing Google auth session...');

        // Exchange session_id for user data via backend
        const response = await apiService.processGoogleSession(sessionId);
        const user = response.user;

        // Store user in localStorage
        localStorage.setItem('user', JSON.stringify(user));
        console.log('Google auth successful:', user.email);

        // Clear the hash and redirect to home
        window.history.replaceState(null, '', window.location.pathname);
        
        // Navigate to home with user data in state
        navigate('/', { 
          replace: true,
          state: { user, authSuccess: true }
        });

      } catch (error) {
        console.error('Auth callback error:', error);
        // Redirect to home with error
        navigate('/', { 
          replace: true,
          state: { authError: 'Authentication failed. Please try again.' }
        });
      }
    };

    processAuth();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 flex items-center justify-center">
      <div className="bg-white rounded-2xl p-12 shadow-lg border border-slate-200 text-center">
        <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center mx-auto mb-6">
          <Shield className="w-10 h-10 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-slate-800 mb-4">
          Completing Sign In
        </h2>
        <div className="flex items-center justify-center gap-3 text-slate-600">
          <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
          <span>Authenticating with Google...</span>
        </div>
      </div>
    </div>
  );
};

export default AuthCallback;
